import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Input } from '@finn-auto/design-system/atoms/input';
import { Checkbox } from '@finn-auto/design-system/atoms/checkbox';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import {
  AlertCircle,
  CalendarCheck,
  ChevronDown,
  Clock,
  Loader2,
  Mail,
  MousePointerClick,
  Plus,
  RefreshCw,
  Save,
  Sparkles,
  Trash2,
  UserPlus,
  XCircle, MessageSquare, Send } from 'lucide-react';
import type { ComponentType } from 'react';
import { cn } from '../../../shared';
import { VariableTextarea } from '../components/VariableTextarea';
import { TemplateBodyEditors } from '../components/TemplateBodyEditors';
import { SlackFormattingHint } from './SlackFormattingHint';
import { EmailFormattingHint } from './EmailFormattingHint';
import { NewTemplateForm, ScopeChip } from './EmailLibraryForm';
import { InMailRulesPanel } from './outreach/InMailRulesPanel';
import { SUBJECT_OVERWRITE_WARNING } from './outreach/subjectWarning';
import {
  CATEGORIES,
  CATEGORY_HINTS,
  CATEGORY_LABELS,
  TOKENS_BY_INTERNAL_KEY,
  isInternalCategory,
  isOutreachCategory,
  isSnippetCategory,
  isSubjectlessOutreach,
  TOKENS_BY_CATEGORY,
  deleteEmailTemplate,
  fetchEmailTemplates,
  saveEmailTemplate,
  type Category,
  type EmailTemplate,
  type Scope,
} from '../services/emailTemplatesApi';

/** Soft, colourful accent per category. */
const CATEGORY_ACCENT: Record<
  Category,
  { head: string; icon: string; ring: string; Icon: ComponentType<{ className?: string }> }
> = {
  // Uniform FINN role-heads (snow + iron), matching the mockup — the category is
  // named in the header, so it needs no per-category colour. The icon distinguishes.
  invite: { head: 'bg-[#F8F8F8]', icon: 'text-gray-500', ring: 'border-gray-200', Icon: Mail },
  // Sent by the booking flow itself — nobody presses send on these.
  booking: { head: 'bg-[#F8F8F8]', icon: 'text-gray-500', ring: 'border-gray-200', Icon: CalendarCheck },
  // The booking page, not an email — these are sentences the candidate reads on screen.
  'booking-page': { head: 'bg-[#F8F8F8]', icon: 'text-gray-500', ring: 'border-gray-200', Icon: MousePointerClick },
  rejection: { head: 'bg-[#F8F8F8]', icon: 'text-gray-500', ring: 'border-gray-200', Icon: XCircle },
  'rejection-later': { head: 'bg-[#F8F8F8]', icon: 'text-gray-500', ring: 'border-gray-200', Icon: XCircle },
  'talent-pool': { head: 'bg-[#F8F8F8]', icon: 'text-gray-500', ring: 'border-gray-200', Icon: Sparkles },
  'checking-in': { head: 'bg-[#F8F8F8]', icon: 'text-gray-500', ring: 'border-gray-200', Icon: Clock },
  // Slack, not email — the one section here that never reaches a candidate.
  internal: { head: 'bg-[#F8F8F8]', icon: 'text-gray-500', ring: 'border-gray-200', Icon: MessageSquare },
  'outreach-inmail': { head: 'bg-[#F8F8F8]', icon: 'text-gray-500', ring: 'border-gray-200', Icon: Send },
  'outreach-follow-up': { head: 'bg-[#F8F8F8]', icon: 'text-gray-500', ring: 'border-gray-200', Icon: Clock },
};

/**
 * The same page serves two sidebar entries (Sofia, Oct 19 2026): the candidate Email Library, and
 * Talent Sourcing › Email Library with only the LinkedIn outreach templates.
 */
const VARIANTS = {
  candidates: {
    title: 'Email Library',
    intro:
      'Whole, ready-to-send candidate emails in German and English. Company templates are shared with the team; your personal templates are private to you. The same library feeds the interview-link invite draft and other automations.',
    categories: CATEGORIES.filter((c) => !isOutreachCategory(c)),
  },
  outreach: {
    title: 'InMail Library',
    intro:
      'Your LinkedIn InMail templates: the wording stays as written, the {placeholders} are filled in per person, and the agent writes only the parts marked AI (the personal sentence, the role paragraph, the location sentence). Below: the rules for those AI parts, your booking link and a sample draft to try a change.',
    categories: CATEGORIES.filter(isOutreachCategory),
  },
} as const;

/**
 * 📧 Email Library — the TA-owned library of whole candidate email templates.
 *
 * Collapsible categories (Invites, Booking confirmations, Rejections, Rejection in
 * later stages, Talent pool, Checking in),
 * each with company templates (shared) and your personal ones. A template is a
 * complete email in DE + EN carrying {variables}; pick one, tweak it, send. The
 * same templates feed the interview-link invite draft and other automations.
 */
export default function EmailLibraryView({ variant = 'candidates' }: { variant?: keyof typeof VARIANTS }) {
  const page = VARIANTS[variant];
  const { data, isPending, error, refetch, isFetching } = useQuery({
    queryKey: ['email-templates'],
    queryFn: fetchEmailTemplates,
  });

  /**
   * `?template=<key>` opens the library ON one template, expanded and scrolled to.
   *
   * Every drafted email in the app that comes FROM this library now links here, because "edit the
   * wording" used to mean leaving the modal, finding the right category among five collapsed
   * sections, and knowing which of its templates produced the text you were looking at.
   *
   * Keyed on `key`, not the Firestore doc id: ids are scope-prefixed
   * (`company__<category>__<key>` / `personal__<owner>__<category>__<key>`), and a recruiter with a
   * personal override of the same wording should see BOTH — which one applies to them is exactly
   * what they need to look at. URL state rather than component state, for the same reason the
   * Requests queue keeps its filters there: it makes the destination linkable.
   */
  const [searchParams] = useSearchParams();
  const focusKey = searchParams.get('template')?.trim().toLowerCase() || null;

  return (
    <div className="mx-auto max-w-4xl px-4 py-6">
      <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-lg bg-[#DBEBFC] text-[#0072EA]">
            <Mail className="h-5 w-5" />
          </span>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">{page.title}</h1>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">{page.intro}</p>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching} className="rounded-full">
          <RefreshCw className={cn('mr-1.5 h-4 w-4', isFetching && 'animate-spin')} /> Refresh
        </Button>
      </header>

      {isPending && (
        <div className="flex items-center gap-2 rounded-lg border border-gray-100 bg-white p-8 text-sm text-gray-500 shadow-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading the library…
        </div>
      )}
      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-red-100 bg-red-50 p-4 text-sm text-red-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          {error instanceof Error ? error.message : 'Could not load the library.'}
        </div>
      )}

      <div className="space-y-4">
        {page.categories.map((category) => (
          <CategorySection
            key={category}
            category={category}
            templates={data ?? []}
            onChanged={refetch}
            focusKey={focusKey}
          />
        ))}
      </div>

      {/* InMails: the templates are the main part, the rules for the AI parts come after (Oct 22 2026). */}
      {variant === 'outreach' && (
        <div className="mt-6">
          <InMailRulesPanel />
        </div>
      )}
    </div>
  );
}

function CategorySection({
  category,
  templates,
  onChanged,
  focusKey,
}: {
  category: Category;
  templates: EmailTemplate[];
  onChanged: () => void;
  /** From `?template=` — the section holding it opens, everything else stays collapsed. */
  focusKey?: string | null;
}) {
  const mine = templates.filter((t) => t.category === category);
  const holdsFocus = Boolean(focusKey && mine.some((t) => t.key?.toLowerCase() === focusKey));
  /**
   * Every section starts collapsed — the library opens as a tidy list of categories. The exception
   * is the one a `?template=` link points into, which would otherwise land on a closed accordion.
   *
   * `null` means "nobody has clicked this header", so the deep link decides. It cannot be a plain
   * `useState(holdsFocus)`: the templates arrive from a query, so `holdsFocus` is false on the first
   * render and true once they load, and the initial value would already have been captured. Deriving
   * it instead of correcting it in an effect avoids the cascading render that would cause — and a
   * click still wins, so a reader can close the section the link opened.
   */
  const [openedByUser, setOpenedByUser] = useState<boolean | null>(null);
  // The InMail Library opens with its templates showing: they are what a recruiter comes to change.
  const open = openedByUser ?? (holdsFocus || isOutreachCategory(category));
  const setOpen = (next: boolean) => setOpenedByUser(next);
  const [adding, setAdding] = useState<Scope | null>(null);
  const [seedId, setSeedId] = useState<string | null>(null);
  const companyTemplates = mine.filter((t) => t.scope === 'company');
  const accent = CATEGORY_ACCENT[category];
  const activeCount = mine.filter((t) => t.active !== false).length;

  const refineAsPersonal = (t: EmailTemplate) => {
    setSeedId(t.id);
    setAdding('personal');
    setOpen(true);
  };
  const closeAdd = () => {
    setAdding(null);
    setSeedId(null);
  };

  return (
    // No `overflow-hidden` here: the "/" variable menu opens below the editor and, in the last
    // template of a section, hangs past the section's bottom edge. Clipping it hid the last variables.
    <section className={cn('rounded-lg border bg-white shadow-sm', accent.ring)}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className={cn('flex w-full items-center gap-3 px-5 py-4 text-left', open ? 'rounded-t-lg' : 'rounded-lg', accent.head)}
      >
        <span className={cn('grid h-9 w-9 place-items-center rounded-full bg-white', accent.icon)}>
          <accent.Icon className="h-4 w-4" />
        </span>
        <span className="flex-1">
          <span className="block text-sm font-semibold text-gray-900">{CATEGORY_LABELS[category]}</span>
          <span className="block text-xs text-gray-500">
            {activeCount} template{activeCount === 1 ? '' : 's'} · {CATEGORY_HINTS[category]}
          </span>
        </span>
        <ChevronDown className={cn('h-5 w-5 text-gray-400 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="space-y-5 px-5 pb-5 pt-1">
          {(['company', 'personal'] as Scope[]).map((scope) => (
            <ScopeGroup
              key={scope}
              scope={scope}
              category={category}
              templates={mine.filter((t) => t.scope === scope)}
              companyTemplates={companyTemplates}
              adding={adding === scope}
              initialSeedId={scope === 'personal' ? seedId : null}
              onAdd={() => setAdding(scope)}
              onCloseAdd={closeAdd}
              onRefineAsPersonal={scope === 'company' ? refineAsPersonal : undefined}
              onChanged={onChanged}
              focusKey={focusKey}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function ScopeGroup({
  scope,
  category,
  templates,
  companyTemplates,
  adding,
  initialSeedId,
  onAdd,
  onCloseAdd,
  onRefineAsPersonal,
  onChanged,
  focusKey,
}: {
  scope: Scope;
  category: Category;
  templates: EmailTemplate[];
  companyTemplates: EmailTemplate[];
  adding: boolean;
  initialSeedId?: string | null;
  onAdd: () => void;
  onCloseAdd: () => void;
  onRefineAsPersonal?: (t: EmailTemplate) => void;
  onChanged: () => void;
  focusKey?: string | null;
}) {
  const sorted = [...templates].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  return (
    <div className="rounded-lg bg-gray-50/70 p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <ScopeChip scope={scope} />
        <Button size="sm" variant="ghost" onClick={onAdd} className="rounded-full text-xs">
          <Plus className="mr-1 h-3.5 w-3.5" /> New template
        </Button>
      </div>

      {adding && (
        <NewTemplateForm
          scope={scope}
          category={category}
          existingKeys={templates.map((t) => t.key)}
          startFrom={scope === 'personal' ? companyTemplates : []}
          initialSeedId={initialSeedId}
          onSaved={() => {
            onCloseAdd();
            onChanged();
          }}
          onCancel={onCloseAdd}
        />
      )}

      {sorted.length === 0 && !adding ? (
        <p className="px-1 py-2 text-xs text-gray-400">
          No {scope} templates yet{scope === 'personal' ? ' — add your own.' : '.'}
        </p>
      ) : (
        <div className="space-y-2">
          {sorted.map((t) => (
            <TemplateCard
              key={t.id}
              template={t}
              onChanged={onChanged}
              onRefineAsPersonal={onRefineAsPersonal ? () => onRefineAsPersonal(t) : undefined}
              focused={Boolean(focusKey && t.key?.toLowerCase() === focusKey)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TemplateCard({
  template,
  onChanged,
  onRefineAsPersonal,
  focused = false,
}: {
  template: EmailTemplate;
  onChanged: () => void;
  onRefineAsPersonal?: () => void;
  /** This is the template a `?template=` link points at: open it, scroll to it, ring it. */
  focused?: boolean;
}) {
  // Same derived-open pattern as the section above: the deep link decides until the reader clicks.
  const [expandedByUser, setExpandedByUser] = useState<boolean | null>(null);
  const expanded = expandedByUser ?? focused;
  const cardRef = useRef<HTMLDivElement>(null);
  // Scrolled to rather than merely opened: the library is five sections deep, so an expanded card
  // below the fold reads as "the link did nothing". No setState here, so no cascading render.
  useEffect(() => {
    if (focused) cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [focused]);
  const [label, setLabel] = useState(template.label);
  const [subjectDe, setSubjectDe] = useState(template.subjectDe);
  const [subjectEn, setSubjectEn] = useState(template.subjectEn);
  const [de, setDe] = useState(template.de);
  const [en, setEn] = useState(template.en);
  const [active, setActive] = useState(template.active);
  const [busy, setBusy] = useState(false);
  const internal = isInternalCategory(template.category);
  /*
    A one-line snippet has no subject either — the booking PAGE is not an email. Same field is
    hidden for both, but for different reasons, so they stay two predicates rather than one
    "hasNoSubject": Slack has no subject line, and a sentence on a web page has nowhere to put one.
  */
  const noSubject = internal || isSnippetCategory(template.category) || isSubjectlessOutreach(template.category);
  // Per KEY, not per category: a nudge has no agenda, and offering {agenda} there would put a
  // token in the menu that its builder never fills — `fmt` then leaves it literal in Slack.
  const tokens = internal
    ? (TOKENS_BY_INTERNAL_KEY[template.key] ?? TOKENS_BY_CATEGORY[template.category])
    : TOKENS_BY_CATEGORY[template.category];

  async function save() {
    // Open InMail drafts follow the Library subject, hand edits too (Sofia, Oct 26 2026): say so first.
    const subjectChanged = subjectDe !== template.subjectDe || subjectEn !== template.subjectEn;
    if (template.category === 'outreach-inmail' && subjectChanged && !window.confirm(SUBJECT_OVERWRITE_WARNING)) return;
    setBusy(true);
    try {
      await saveEmailTemplate({
        key: template.key,
        scope: template.scope,
        category: template.category,
        label,
        order: template.order,
        // Blanked rather than merely hidden: a category that stops having subjects must not
        // keep shipping the one its template carried from before.
        subjectDe: noSubject ? '' : subjectDe,
        subjectEn: noSubject ? '' : subjectEn,
        de,
        en,
        active,
      });
      showToast({ status: 'success', description: `Saved “${label}”.` });
      // Explicitly false, not "back to whatever the link said" — a saved template collapses even
      // when this is the card a `?template=` link opened.
      setExpandedByUser(false);
      onChanged();
    } catch (e) {
      showToast({ status: 'error', description: e instanceof Error ? e.message : 'Could not save.' });
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!window.confirm(`Delete “${label}”?`)) return;
    setBusy(true);
    try {
      await deleteEmailTemplate(template.id);
      showToast({ status: 'success', description: `Deleted “${label}”.` });
      onChanged();
    } catch (e) {
      showToast({ status: 'error', description: e instanceof Error ? e.message : 'Could not delete.' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      ref={cardRef}
      className={cn(
        'rounded-lg border bg-white p-3 shadow-sm transition',
        active ? 'border-gray-100' : 'border-dashed border-gray-200 opacity-70',
        // Says "this is the one you clicked", which a scroll position alone does not.
        focused && 'ring-2 ring-[#0072EA] ring-offset-2'
      )}
    >
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setExpandedByUser(!expanded)}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          <ChevronDown className={cn('h-4 w-4 shrink-0 text-gray-400 transition-transform', expanded && 'rotate-180')} />
          <span className="truncate text-sm font-medium text-gray-800">{template.label}</span>
          {!active && <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-500">inactive</span>}
        </button>
        {onRefineAsPersonal && (
          <button
            type="button"
            onClick={onRefineAsPersonal}
            title="Refine & save as your personal template"
            className="flex shrink-0 items-center gap-1 rounded-full border border-purple-200 bg-purple-50 px-2.5 py-1 text-[11px] font-medium text-purple-700 hover:bg-purple-100"
          >
            <UserPlus className="h-3 w-3" /> Refine as personal
          </button>
        )}
      </div>

      {!expanded && <p className="mt-1 truncate pl-6 text-xs text-gray-400">{template.de || template.en}</p>}

      {expanded && (
        <div className="mt-3 space-y-3 pl-6">
          <Input label="Label" value={label} onChange={(e) => setLabel((e.target as HTMLInputElement).value)} className="rounded-xl" />
          {!noSubject && (
            <div className="grid gap-2 sm:grid-cols-2">
              <Input label="Subject (DE)" value={subjectDe} onChange={(e) => setSubjectDe((e.target as HTMLInputElement).value)} className="rounded-xl" />
              <Input label="Subject (EN)" value={subjectEn} onChange={(e) => setSubjectEn((e.target as HTMLInputElement).value)} className="rounded-xl" />
            </div>
          )}
          {noSubject ? (
            <>
              <VariableTextarea label="German" tokens={tokens} rows={10} value={de} onChange={setDe} />
              <VariableTextarea label="English" tokens={tokens} rows={10} value={en} onChange={setEn} />
            </>
          ) : (
            <TemplateBodyEditors tokens={tokens} rows={10} de={de} en={en} setDe={setDe} setEn={setEn} />
          )}
          {internal && <SlackFormattingHint />}
          {!noSubject && <EmailFormattingHint />}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Checkbox label="active" labelClassName="text-xs text-gray-500" checked={active} onCheckedChange={(v) => setActive(v === true)} />
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={remove} disabled={busy} className="rounded-full text-[#E71D3F]">
                <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete
              </Button>
              <Button size="sm" onClick={save} disabled={busy || !label} className="rounded-full">
                <Save className="mr-1 h-3.5 w-3.5" /> {busy ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
